
import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Typography, Button, Modal, Select } from "antd";
import { useDispatch } from "react-redux";
import { deleteRow, getData } from "../../../../actions/apiCall";
import { DeleteOutlined } from "@ant-design/icons";
import { postData, putData } from "../../../../actions/developer";
import InfiniteScrollList from "../../../../components/Forms/ListView/InfiniteScrollList";
import RenderFormItem from "../../../../components/Forms/FormItems";
import { FormColumnConfig, MasterConfigs } from "../../../../interfaces/entity";
import FormSetup from "../../../../components/Forms/FormSetup";
import MasterComponent from "../../../../components/Forms";
import { on } from "events";

interface formTabsProps {
    record: any;
}

const FormTabs: React.FC<formTabsProps> = ({ record }) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState(null);
    const [loading, setLoading] = useState(false);
    const [assignFormModal, setAssignFormModal] = useState(false);
    const [formPreview, setFormPreview] = useState(false);
    const [prevConfig, setPrevConfig] = useState<any>(null);

    const [tabs, setTabs] = useState([]);

    useEffect(() => {
        if (activeTab) {
            setLoading(true);
        }
    }, [activeTab]);

    const onFormStatusChange = async (status, formId) => {
        if (status === "") return;
        const resData = await dispatch(putData(`/form-tab-mappings/bulk/update`, [{
            updateOne: {
                filter: { formConfigs: formId._id, formTabs: activeTab },
                update: { $set: { status: status === "unassigned" ? "deleted" : 'active' } },
                upsert: true
            }
        }]));
        if (resData) {
            setLoading(true);
        }
    }

    useEffect(() => {
        if (record) {
            getTabs(record._id)
        }
    }, [record]);

    const getTabs = async (id) => {
        const resData = await dispatch(getData(`/form-tabs-settings`, { doccumentFolder: id, pageSize: 100 }));
        if (resData) {
            setTabs(resData?.data.sort((a, b) => a.order - b.order));
            setActiveTab(resData?.data[0]?._id);
        }
    }

    const addTab = async (tab) => {
        const resData = await dispatch(postData(`/form-tabs-settings`, {
            doccumentFolder: record._id,
            name: tab.name,
            order: tabs.length + 1
        }));
        if (resData) {
            getTabs(record._id)
        }
    }

    const onDelete = async (tab) => {
        const resData = await dispatch(deleteRow(`/form-tabs-settings/${tab._id}`));
        if (resData) {
            getTabs(record._id)
        }
    };

    const updateBulk = async (tabs) => {
        const updateTabs = tabs.map((tab) => {
            return {
                updateOne: {
                    filter: { _id: tab._id },
                    update: { $set: { order: tab.order } }
                }
            }
        });

        const resData = await dispatch(putData(`/form-tabs-settings/bulk/update`, updateTabs));
        if (resData) {
            getTabs(record._id)
        }
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const newTabs = Array.from(tabs);
        const [reorderedTab] = newTabs.splice(result.source.index, 1);
        newTabs.splice(result.destination.index, 0, reorderedTab);
        newTabs.forEach((tab, index) => {
            tab.order = index + 1;
        });
        updateBulk(newTabs);
        setTabs(newTabs);
    };

    const handleTitleChange = async (id, newTitle) => {
        const resData = await dispatch(putData(`/form-tabs-settings/${id}`, {
            name: newTitle
        }));
        if (resData) {
            getTabs(record._id)
        }
    };

    const addNewTab = () => {
        addTab({ name: `Tab ${tabs.length + 1}` });
    };

    const formConfig: MasterConfigs = {
        api: () => `/form-configs`,
        modalTitle: (action: string) => `${action} Category`,
        getColumns: () => {
            return [
                {
                    title: "Form Name",
                    dataIndex: "name",
                    key: "name",
                    width: 250
                },
                {
                    title: "Route Name",
                    dataIndex: "routeName",
                    key: "routeName",
                },
                {
                    title: "Model Title",
                    dataIndex: "modelTitle",
                    key: "modelTitle",
                },
                {
                    title: "Form Mapping Status",
                    dataIndex: "",
                    key: "",
                    align: "center",
                    width: 200,
                    render: (text, record) => <Select
                        allowClear
                        style={{ width: 120 }}
                        onChange={(e) => onFormStatusChange(e, record)}
                    >
                        <Select.Option value="">Select Status</Select.Option>
                        <Select.Option value="assigned">Assigned</Select.Option>
                        <Select.Option value="unassigned">UnAssigned</Select.Option>
                    </Select>
                }
            ]
        },
    }


    const config: MasterConfigs = {
        api: () => `/form-category`,
        modalTitle: (action: string) => `Form Mapping`,
        getFormColumns: () => {
            return [
                {
                    type: "custom",
                    span: 24,
                    formItem: () => <MasterComponent config={formConfig} />
                }
            ]
        },
        onSubmit: async (id, values) => {
            console.log(id, values);
            return await { data: {}, status: 200, code: 200 };
        },
        combo: [
            {
                name: "category",
                api: "/form-category",
                idKey: "_id",
                render: (e) => e.name
            }
        ]
    }

    return (
        <div className="w-full p-4">
            <div className="flex items-center border-b border-gray-100 gap-4 overflow-hidden">
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="tabs" direction="horizontal">
                        {(provided) => (
                            <div
                                className="flex overflow-x-auto space-x-2 flex-grow scrollbar-hide"
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                {tabs.map((tab, index) => (
                                    <Draggable key={tab._id} draggableId={tab._id} index={index}>
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className="rounded-md cursor-pointer whitespace-nowrap"
                                            >
                                                <div className={`flex items-center min-w-[150px] text-center justify-center
                                                     gap-4 mt-2 ${activeTab === tab._id ? 'border-b-2 border-b-blue-900' : ''}`}
                                                    onClick={() => setActiveTab(tab._id)}>
                                                    <Typography.Title
                                                        level={5}
                                                        editable={{
                                                            onChange: (newTitle) => handleTitleChange(tab._id, newTitle),
                                                        }}
                                                        className="m-0"
                                                    >
                                                        {tab.name}
                                                    </Typography.Title>
                                                    {activeTab === tab._id && <Button
                                                        icon={<DeleteOutlined />}
                                                        type="text"
                                                        danger
                                                        onClick={() => onDelete(tab)}
                                                    />}
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                <Button className="ml-2 flex-shrink-0" type="primary" onClick={addNewTab}>
                    Add Tab
                </Button>
            </div>
            <div className="mt-4 w-full">
                <Button className="mb-4" onClick={() => setAssignFormModal(true)}>Assign Form</Button>
                <InfiniteScrollList
                    apiUrl="/form-tab-mappings/list/forms"
                    apiQuery={{ formTabs: activeTab }}
                    renderItem={(item) => <div className="flex items-center justify-between border border-gray-100 p-2" >
                        <div>
                            <Typography.Text strong>{item.formConfigs?.name}</Typography.Text>
                        </div>
                        <div>
                            <Button type="link" onClick={() => {
                                setFormPreview(item)
                                setPrevConfig(item.formConfigs)
                            }}>Preview</Button>
                            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onFormStatusChange('unassigned', item._id)} />
                        </div>
                    </div>}
                    height={500}
                    reload={loading}
                    onLoaded={() => setLoading(false)}
                />
            </div>
            <FormSetup config={config} open={assignFormModal} onClose={() => setAssignFormModal(false)} />
            <FormSetup config={{
                api: () => prevConfig?.routeName,
                modalTitle: (action: string) => `${action} ${prevConfig?.modelTitle}`,
                getFormColumns: () => prevConfig?.fields ?? []
            } as MasterConfigs}
                open={prevConfig && formPreview}
                onClose={() => setFormPreview(false)}
                isModal={true}
            />
        </div>
    );
};

export default FormTabs;
